import { Table, TableSorting } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import React, { useMemo } from "react";
import { FCC } from "@/types/common";
import { Stack, Typography } from "@mui/material";
import { EMPTY_STR } from "@/constants/string";
import { IconActionStar } from "@/assets/icons";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { MRT_ColumnDef } from "material-react-table";
import { TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { CaseTalentAnotherStatus } from "@/features/case/components/CaseMatches/CaseTalentAnotherStatus";

type TCaseMatchTableProps = {
  rows: TTalentListOutputDto[];
};

export const CaseEditMatchTable: FCC<TCaseMatchTableProps> = ({ rows }) => {
  const { data: genderOptions } = useEnum("EGender");

  const columns = useMemo<MRT_ColumnDef<TTalentListOutputDto>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Header: <TableSorting name="talentNumber">人材管理番号</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{row.original?.talentNumber || EMPTY_STR.TEXT}</Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        header: "名前",
        Header: <TableSorting name="fullName">名前</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              {row.original.isRecommendedTalent && <IconActionStar />}
              <Typography variant="body2">{row.original?.fullName || EMPTY_STR.TEXT}</Typography>
            </Stack>
          );
        },
      },
      {
        id: "nickName",
        header: "呼び名",
        Header: <TableSorting name="nickName">呼び名</TableSorting>,
        Cell: ({ row }) => row.original.nickName,
      },
      {
        id: "status",
        header: "ステータス",
        Cell: ({ row: _row }) => <CaseTalentAnotherStatus isAnotherStatus={true} />,
      },
      {
        id: "nationalityName",
        accessorKey: "nationality.nationalityName",
        header: "国籍",
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{row.original?.nationality?.nationalityName || EMPTY_STR.TEXT}</Typography>
            </Stack>
          );
        },
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender) || EMPTY_STR.TEXT,
      },
      {
        id: "age",
        header: "年齢",
        Cell: ({ row }) => {
          const {
            original: { birthday },
          } = row;
          if (!birthday) return EMPTY_STR.TEXT;
          const today = new Date();
          const birthDate = new Date(String(birthday));

          let age = today.getFullYear() - birthDate.getFullYear();

          const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

          if (!hasHadBirthdayThisYear) {
            age--;
          }

          return age;
        },
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.residenceStatus?.residenceStatus || EMPTY_STR.TEXT,
        Header: <TableSorting name="residenceStatus.residenceStatus">在留資格</TableSorting>,
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => row.original.languageCertificates[0]?.languageTestLevel?.level || EMPTY_STR.TEXT,
        Header: <TableSorting name="languageTestLevel.level">日本語能力</TableSorting>,
      },
      {
        id: "personInChargeId",
        header: "担当者",
        accessorKey: "personInCharge.fullName",
        Header: <TableSorting name="personInCharge.fullName">担当者</TableSorting>,
      },
    ],
    [],
  );
  const isLoading = false;
  const { table } = useTable({
    columns,
    rows,
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    state: {},
  });

  return <Table table={table} isLoading={isLoading} maxHeight={440} />;
};
